import { useCallback } from "react"
import { AggregationType } from "~/ui-rtk/constants/charts"
import { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import {
  TColumnFiltersState,
  TableVisualizationColumn,
} from "~/ui-rtk/constants/table-visualization-types"
import { useCSVExportWorker } from "./use-csv-export-worker"
import { buildExportQuery } from "~/ui-rtk/utils/export-query-builder"
import { createSerializableColumnConfig } from "~/ui-rtk/utils/csv-export"
import useCubeFiltersFromColumnFilters from "./cube-filters-from-column-filters"
import { SortingState } from "@tanstack/react-table"
import dayjs from "dayjs"

export interface UseChartCSVExportOptions {
  chartId: string
  dateRange: [Date, Date]
  compareRange?: [Date, Date]
  granularity: AggregationType
  cubeFilters?: TCubeFilterOptions
  columnFilters?: TColumnFiltersState
  sorting?: SortingState
  queryPrefix?: string | null
  columnConfig: TableVisualizationColumn[]
  defaultFilename?: string
}

export function useChartCSVExport(options: UseChartCSVExportOptions) {
  const {
    chartId,
    dateRange,
    compareRange,
    granularity,
    cubeFilters,
    columnFilters,
    sorting,
    queryPrefix,
    columnConfig,
    defaultFilename = "export",
  } = options

  const { isExporting, progress, exportToCSV, resetExport } =
    useCSVExportWorker()

  // Convert column filters to cube filters
  const extraFilters = useCubeFiltersFromColumnFilters(
    queryPrefix,
    columnFilters,
  )

  const handleExportCSV = useCallback(
    (customFilename?: string) => {
      if (!chartId || !columnConfig.length) {
        console.warn("No chart ID or column configuration for export")
        return
      }

      try {
        resetExport()

        // Combine cube filters with column filters for export
        const combinedCubeFilters = {
          ...cubeFilters,
          extraFilters, // Array of individual filter objects
        }

        // Build the export query without pagination
        const exportQuery = buildExportQuery({
          chartId,
          dateRange,
          granularity,
          cubeFilters: combinedCubeFilters,
          sorting,
          queryPrefix: queryPrefix || undefined,
        })

        // Build compare query if compareRange is provided
        const compareQuery = compareRange
          ? buildExportQuery({
              chartId,
              dateRange: compareRange,
              granularity,
              cubeFilters: combinedCubeFilters,
              sorting,
              queryPrefix: queryPrefix || undefined,
            })
          : undefined

        // Transform column config keys to include query prefix to match Cube data field names
        const transformedColumnConfig = columnConfig.map(
          (column: TableVisualizationColumn) => ({
            ...column,
            key:
              queryPrefix && !column.key.includes(queryPrefix)
                ? `${queryPrefix}.${column.key}`
                : column.key,
          }),
        )

        // Create a serializable version of column config (remove functions)
        const serializableColumnConfig = createSerializableColumnConfig(
          transformedColumnConfig,
        )

        const filename =
          customFilename ||
          `${defaultFilename}-${dayjs().format("YYYY-MM-DD")}.csv`

        exportToCSV(exportQuery, serializableColumnConfig, {
          filename,
          includeHeaders: true,
          compareQuery,
        })
      } catch (error) {
        console.error("Error exporting CSV:", error)
      }
    },
    [
      chartId,
      columnConfig,
      dateRange,
      compareRange,
      granularity,
      cubeFilters,
      extraFilters,
      sorting,
      queryPrefix,
      defaultFilename,
      exportToCSV,
      resetExport,
    ],
  )

  return {
    isExporting,
    progress,
    exportCSV: handleExportCSV,
    resetExport,
  }
}
